import { Link } from "react-router-dom";
import { MaskedLine, FadeUp } from "@/components/motion";

const STEPS = [
  { n: "01", title: "Tell us what you need", desc: "Choose an amount and share a few details about yourself, your income and your banking. The request takes minutes, not afternoons." },
  { n: "02", title: "We scan the network", desc: "Your request is reviewed in near real-time by lending partners whose criteria match your profile — no shotgun applications to dozens of lenders." },
  { n: "03", title: "Review your offer", desc: "If a partner can help, you're shown their agreement up front: the rate, the total repayment and the schedule. Read everything before you commit to anything." },
  { n: "04", title: "Funds land directly", desc: "Accept an offer and funds are typically sent by ACH straight to your checking account within one to five business days. Repayment terms usually run 6 to 60 months." },
];

const REQUIREMENTS = [
  { title: "Age & Residency", body: "18 or older, with a valid Social Security number and U.S. citizenship or permanent residency." },
  { title: "Income", body: "Full-time employment, self-employment, or regular disability / Social Security benefits. Some partners may ask for a pay stub." },
  { title: "Bank Account", body: "An active checking account in your name — approved funds are deposited directly into it." },
  { title: "Credit Pattern", body: "Any score may apply. Partners look for recent responsibility: no active bankruptcies and no accounts 60+ days past due." },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <section className="px-6 pt-40 pb-20" data-testid="hiw-hero">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-8">
            <MaskedLine delay={0.1}>The Process</MaskedLine>
          </p>
          <h1 className="font-display font-light text-5xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl text-[#0F1F1A]">
            <MaskedLine delay={0.25}>Four steps.</MaskedLine>
            <MaskedLine delay={0.45}>Zero <span className="italic text-[#0C6B4F]">guesswork.</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8 max-w-xl">
            <span className="text-[#5C6B62] leading-relaxed">
              Our comparison process connects you with a lender or financial service provider from our vetted network. If approved, money can arrive as fast as the next business day.
            </span>
          </MaskedLine>
        </div>
      </section>

      <section className="px-6 pb-24" data-testid="hiw-steps">
        <div className="max-w-7xl mx-auto">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.05} className="border-t border-[#DFE5DC] py-12 grid md:grid-cols-[120px_1fr_2fr] gap-6 items-baseline">
              <span className="font-display text-5xl md:text-6xl text-[#C9A227] font-light">{s.n}</span>
              <h3 className="font-display text-2xl md:text-3xl text-[#0F1F1A]">{s.title}</h3>
              <p className="text-[#5C6B62] leading-relaxed text-sm md:text-base">{s.desc}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="px-6 py-24 border-t border-[#DFE5DC] bg-white" data-testid="hiw-requirements">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-4">Before you apply</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-16 max-w-2xl text-[#0F1F1A]">What providers look for.</h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-px bg-[#DFE5DC] border border-[#DFE5DC]">
            {REQUIREMENTS.map((r, i) => (
              <FadeUp key={r.title} delay={i * 0.05} className="h-full">
                <div className="bg-white hover:bg-[#EDF2EA] transition-colors duration-500 p-10 h-full">
                  <h4 className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#0C6B4F] mb-4">{r.title}</h4>
                  <p className="text-[#5C6B62] text-sm leading-relaxed">{r.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-8 font-mono2 text-xs text-[#8A978D] uppercase tracking-[0.15em]">
              Meeting these requirements does not guarantee approval or connection with a provider.
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <Link to="/#apply" data-testid="hiw-cta" className="mt-12 inline-block bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-[#0A5C43] active:scale-[0.98] transition-[background-color,transform] duration-300">
              Compare My Options
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
